import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const EMPTY = { account_number: "", description: "", default_payment_terms_id: "", default_vat_rate_id: "", include_in_group_overhead: true };
const SPECIAL_ACCOUNTS = new Set(["6591", "6064"]);

export default function CostAccountsPage() {
  const { isAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [terms, setTerms] = useState([]);
  const [rates, setRates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const termName = (id) => terms.find((t) => t.id === id)?.name || "—";
  const rateName = (id) => rates.find((r) => r.id === id)?.name || "—";

  const load = async () => {
    const [a, t, v] = await Promise.all([
      api.get("/cost-accounts"),
      api.get("/payment-terms"),
      api.get("/vat/rates"),
    ]);
    setAccounts(a.data);
    setTerms(t.data);
    setRates(v.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      account_number: a.account_number,
      description: a.description || "",
      default_payment_terms_id: a.default_payment_terms_id || "",
      default_vat_rate_id: a.default_vat_rate_id || "",
      include_in_group_overhead: a.include_in_group_overhead !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      description: form.description || null,
      default_payment_terms_id: form.default_payment_terms_id || null,
      default_vat_rate_id: form.default_vat_rate_id || null,
    };
    try {
      if (editing) {
        await api.patch(`/cost-accounts/${editing.id}`, payload);
        toast.success("Cost account updated");
      } else {
        await api.post("/cost-accounts", payload);
        toast.success("Cost account created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/cost-accounts/${a.id}`, { active: !a.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="cost-accounts-page">
      <PageHeader
        title="Cost Accounts"
        subtitle="Shared account plan used for matching against actuals"
        actions={
          isAdmin && (
            <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-cost-account-button">
              <Plus className="h-4 w-4 mr-1.5" /> New account
            </Button>
          )
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="cost-accounts-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Account</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Default payment terms</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Default VAT</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`cost-account-row-${a.account_number}`}>
                <td className="py-3 px-4">
                  <span className="font-semibold text-[#0052CC] text-sm">{a.account_number}</span>
                  {a.description && <span className="text-xs text-slate-500 block">{a.description}</span>}
                  {!SPECIAL_ACCOUNTS.has(a.account_number) && a.include_in_group_overhead !== false && (
                    <span className="mt-1 inline-flex items-center rounded bg-[#EBF3FE] text-[#0047B3] text-[9px] font-bold px-1.5 py-px" data-testid={`cost-account-group-oh-${a.account_number}`}>GROUP OH</span>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-slate-700">{termName(a.default_payment_terms_id)}</td>
                <td className="py-3 px-4 text-sm text-slate-700">{rateName(a.default_vat_rate_id)}</td>
                <td className="py-3 px-4"><ActiveBadge active={a.active} testId={`cost-account-status-${a.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(a)} data-testid={`edit-cost-account-${a.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(a)} data-testid={`toggle-cost-account-${a.id}`}>
                        {a.active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {accounts.length === 0 && <EmptyState title="No cost accounts yet" testId="cost-accounts-empty" />}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="cost-account-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit cost account" : "New cost account"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Account number</Label>
                <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="6591" data-testid="cost-account-number-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cost-account-description-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Default payment terms</Label>
                <Select value={form.default_payment_terms_id} onValueChange={(v) => setForm({ ...form, default_payment_terms_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cost-account-terms-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {terms.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Default VAT rate</Label>
                <Select value={form.default_vat_rate_id} onValueChange={(v) => setForm({ ...form, default_vat_rate_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cost-account-vat-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {rates.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.name} ({r.rate}%)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={`flex items-center gap-2 text-sm ${SPECIAL_ACCOUNTS.has(form.account_number) ? "text-slate-400" : "text-slate-700"}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#0052CC]"
                  checked={!SPECIAL_ACCOUNTS.has(form.account_number) && form.include_in_group_overhead !== false}
                  disabled={SPECIAL_ACCOUNTS.has(form.account_number)}
                  onChange={(e) => setForm({ ...form, include_in_group_overhead: e.target.checked })}
                  data-testid="cost-account-include-oh-checkbox"
                />
                Include in Group Overhead
              </label>
              <p className="text-xs text-slate-500" data-testid="cost-account-include-oh-help">
                {SPECIAL_ACCOUNTS.has(form.account_number)
                  ? "Not applicable — this account has special canonical handling (factoring / system Group Overhead)."
                  : form.include_in_group_overhead !== false
                    ? "When enabled, costs on this account are included in the Group Overhead pool allocated across Elva companies."
                    : "The cost remains in Elva Group but is excluded from Group Overhead allocation."}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cost-account-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="cost-account-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
